import React, { useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';

function VideoRecorderComponent({ onReset }) {
  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    previewStream,
    clearBlobUrl
  } = useReactMediaRecorder({
    video: true,
    audio: true,
    askPermissionOnMount: true,
    blobPropertyBag: { type: 'video/webm' },
    mediaRecorderOptions: {
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 2500000,
      mimeType: 'video/webm'
    }
  });

  const handleReset = () => {
    onReset();
  };

  const handleDownload = () => {
    if (mediaBlobUrl) {
      const a = document.createElement('a');
      a.href = mediaBlobUrl;
      a.download = 'video-introduction.webm';
      a.click();
    }
  };

  return (
    <>
      {/* Status Display */}
      <div className="mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-blue-700 font-medium">Status: {status}</p>
      </div>
      
      {/* Video Display */}
      <div className="w-full max-w-xl mb-6">
        {!mediaBlobUrl ? (
          <div className="relative">
            <div className="w-full h-auto rounded-lg border-2 border-gray-300 bg-black overflow-hidden" style={{ minHeight: '360px' }}>
              {previewStream && (
                <video 
                  ref={(video) => {
                    if (video && previewStream) {
                      video.srcObject = previewStream;
                    }
                  }}
                  autoPlay 
                  playsInline 
                  muted 
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              )}
            </div>
            
            {status === 'recording' && (
              <div className="absolute top-4 right-4 flex items-center bg-red-500 text-white px-3 py-1 rounded-full">
                <div className="w-3 h-3 bg-white rounded-full mr-2 animate-pulse"></div>
                <span>Recording...</span>
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            <video 
              src={mediaBlobUrl}
              controls 
              autoPlay
              playsInline
              className="w-full h-auto rounded-lg border-2 border-gray-300"
              style={{ minHeight: '360px' }}
            />
          </div>
        )}
      </div>
      
      {/* Control Buttons */}
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {!mediaBlobUrl ? (
          status !== 'recording' ? (
            <button 
              onClick={startRecording}
              disabled={status === 'acquiring_media'}
              className={`px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors duration-200 shadow-md flex items-center ${
                status === 'acquiring_media' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <circle cx="12" cy="12" r="4" fill="currentColor" />
              </svg>
              {status === 'acquiring_media' ? 'Getting Camera...' : 'Start Recording'}
            </button>
          ) : (
            <button 
              onClick={stopRecording} 
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transition-colors duration-200 shadow-md flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="6" width="12" height="12" strokeWidth="2" />
              </svg>
              Stop Recording
            </button>
          )
        ) : (
          <>
            <button 
              onClick={handleReset} 
              className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors duration-200 shadow-md flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Record Again
            </button>
            <button 
              onClick={handleDownload}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-medium rounded-lg hover:from-green-600 hover:to-green-700 transition-colors duration-200 shadow-md flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Video
            </button>
          </>
        )}
      </div>

      {/* Debug Info */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg w-full max-w-xl">
        <h3 className="font-semibold mb-2">Debug Information:</h3>
        <p><strong>Status:</strong> {status}</p>
        <p><strong>Has Preview Stream:</strong> {previewStream ? 'Yes' : 'No'}</p>
        <p><strong>Has Recorded Video:</strong> {mediaBlobUrl ? 'Yes' : 'No'}</p>
        {mediaBlobUrl && (
          <p><strong>Video URL:</strong> {mediaBlobUrl.substring(0, 50)}...</p>
        )}
      </div>
    </>
  );
}

function VideoIntroRMR() {
  const [recorderKey, setRecorderKey] = useState(0);

  const handleReset = () => {
    // Force complete remount of the recorder component
    setRecorderKey(prev => prev + 1);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6">
      <div className="w-full max-w-[800px] bg-white rounded-lg shadow-md p-8 flex flex-col items-center">
        <h2 className="text-3xl font-semibold mb-2 text-center">Video Introduction Test (React Media Recorder)</h2>
        <p className="text-lg text-gray-600 mb-6 text-center px-12">
          Testing the react-media-recorder library for video recording functionality.
        </p>
        
        <VideoRecorderComponent key={recorderKey} onReset={handleReset} />
        
        {/* Tips Section */}
        <div className="mt-8 text-gray-600 text-center max-w-xl text-lg">
          <p className="mb-2 font-medium">Tips for a great introduction:</p>
          <ul className="list-disc list-inside text-left">
            <li>Find a quiet place with good lighting</li>
            <li>Speak clearly and at a moderate pace</li>
            <li>Briefly mention your background and experience</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default VideoIntroRMR;
